import { slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { scaleOrdinal, schemeCategory20 } from 'd3-scale';
import { pie } from 'd3-shape';
import Arc from './Arc.js';

// Extra space allotted for labels
var LABEL_MARGIN = 15;
var ADJUSTMENT_UNIT = 0.175;
function doesIntersect(r1, r2) {
  if (!r1.height || !r1.width || !r2.height || !r2.width) {
    return false;
  }
  return !(r2.left > r1.right || r2.right < r1.left || r2.top > r1.bottom || r2.bottom < r1.top);
}
var getIdentifierFromReactNode = function getIdentifierFromReactNode(node) {
  return typeof node === 'string' ? node : node.id;
};
var getRects = function getRects(elements) {
  var rects = {};
  elements.forEach(function (element) {
    var key = element.getAttribute('data-key');
    if (!key) {
      throw new Error("Expected a key while adjusting arc labels but got: " + key);
    }
    rects[key] = element.getBoundingClientRect();
  });
  return rects;
};
var getPieArcs = pie().sort(null);
function DataSeries(_ref) {
  var data = _ref.data,
    centerValue = _ref.centerValue,
    centerText = _ref.centerText,
    valueFormatter = _ref.valueFormatter,
    smallCenterLabel = _ref.smallCenterLabel,
    transform = _ref.transform,
    labels = _ref.labels,
    radius = _ref.radius,
    innerRadius = _ref.innerRadius,
    colors = _ref.colors,
    labelTextFill = _ref.labelTextFill,
    valueTextFill = _ref.valueTextFill,
    hideLabels = _ref.hideLabels,
    width = _ref.width;
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    offsets = _useState2[0],
    setOffsets = _useState2[1];
  var containerRef = useRef();

  // We want to re-calculate these offsets on every render.
  // We avoid an infinite render loop by only updating state
  // when needed.
  // eslint-disable-next-line zillow/react-hooks/exhaustive-deps
  useEffect(function () {
    if (hideLabels) {
      return;
    }
    var container = containerRef.current;
    var parentRect = container.parentNode.getBoundingClientRect();
    var doesOverlap = function doesOverlap(currentRect, previousRect) {
      return parentRect.left - LABEL_MARGIN > currentRect.left || parentRect.right + LABEL_MARGIN < currentRect.right || previousRect && doesIntersect(currentRect, previousRect);
    };
    var labelElements = _toConsumableArray(container.querySelectorAll('.arc-label-group'));
    /**
     * Go through each label, checking if it intersects with any previous label.
     * If so, keep moving it along until it does not intersect
     */
    var rects = getRects(labelElements);
    var newOffsets = {};
    labelElements.forEach(function (element, index) {
      var currentRect = rects[element.getAttribute('data-key')];
      var label = element.querySelector('.arc-label-text').getAttribute('data-label');
      var previousElements = labelElements.slice(0, index);
      previousElements.forEach(function (prevElement) {
        var previousRect = rects[prevElement.getAttribute('data-key')];
        var doOverlap = doesOverlap(currentRect, previousRect);
        if (doOverlap) {
          var _offsets$label;
          newOffsets[label] = ((_offsets$label = offsets[label]) !== null && _offsets$label !== void 0 ? _offsets$label : 0) + ADJUSTMENT_UNIT;
        }
      });
    });
    // update offsets only if at least one is different, to avoid unnecessary renders
    if (Object.entries(newOffsets).some(function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 2),
        key = _ref3[0],
        value = _ref3[1];
      return offsets[key] !== value;
    })) {
      setOffsets(Object.assign({}, offsets, newOffsets));
    }
  });
  return /*#__PURE__*/React.createElement("g", {
    ref: containerRef,
    className: "pie-group",
    transform: transform
  }, getPieArcs(data).map(function (arc, i) {
    return /*#__PURE__*/React.createElement(Arc, {
      startAngle: arc.startAngle,
      endAngle: arc.endAngle,
      outerRadius: radius,
      innerRadius: innerRadius,
      labelTextFill: labelTextFill,
      valueTextFill: valueTextFill,
      fill: colors(i),
      label: labels[i],
      labelOffset: offsets[getIdentifierFromReactNode(labels[i])],
      hideLabels: hideLabels,
      value: data[i],
      valueFormatter: valueFormatter
      // eslint-disable-next-line zillow/react/no-array-index-key
      ,
      key: i,
      dataKey: i,
      width: width
    });
  }), smallCenterLabel ? /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("text", {
    id: "pie-group-center-text",
    style: {
      textAnchor: 'middle',
      fontSize: '11px',
      textTransform: 'uppercase'
    },
    y: "-5"
  }, centerText), /*#__PURE__*/React.createElement("text", {
    id: "pie-group-center-value",
    style: {
      textAnchor: 'middle',
      fontSize: '20px',
      fontWeight: 'bold'
    },
    y: "20"
  }, valueFormatter(centerValue))) : /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("text", {
    style: {
      textAnchor: 'middle',
      fontSize: '15px',
      fontWeight: 'bold'
    },
    y: "-10"
  }, centerText), /*#__PURE__*/React.createElement("text", {
    style: {
      textAnchor: 'middle',
      fontSize: '28px'
    },
    y: "20"
  }, valueFormatter(centerValue))));
}
DataSeries.propTypes = process.env.NODE_ENV !== "production" ? {
  centerText: PropTypes.string,
  colors: PropTypes.func,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  data: PropTypes.array,
  innerRadius: PropTypes.number,
  radius: PropTypes.number,
  transform: PropTypes.string,
  hideLabels: PropTypes.bool,
  smallCenterLabel: PropTypes.bool,
  centerValue: PropTypes.number,
  valueFormatter: PropTypes.func,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  labels: PropTypes.array,
  labelTextFill: PropTypes.string,
  valueTextFill: PropTypes.string,
  width: PropTypes.number
} : {};
DataSeries.defaultProps = {
  innerRadius: 0,
  data: [],
  colors: scaleOrdinal(schemeCategory20)
};

export { DataSeries as default };
